import { Search, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { AvatarTile } from '@/ui/atoms/avatar-tile'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/molecules/dialog'

const emojiChoices = [
  ['🧪', 'Test tube', 'test experiment'],
  ['🧭', 'Compass', 'navigation direction'],
  ['🔎', 'Magnifying glass', 'search inspect'],
  ['🛰️', 'Satellite', 'network remote'],
  ['🪴', 'Plant', 'growth calm'],
  ['🧱', 'Brick', 'build structure'],
  ['🎛️', 'Controls', 'settings configuration'],
  ['🗂️', 'Files', 'folder organize'],
  ['🔐', 'Lock', 'security authentication'],
  ['🧰', 'Toolbox', 'tools repair'],
  ['⚙️', 'Gear', 'settings system'],
  ['🪄', 'Magic wand', 'automation magic'],
  ['🧵', 'Thread', 'conversation async'],
  ['🪟', 'Window', 'browser desktop'],
  ['🗺️', 'Map', 'navigation route'],
  ['🔭', 'Telescope', 'observe explore'],
  ['💡', 'Light bulb', 'idea insight'],
  ['🧠', 'Brain', 'ai intelligence'],
  ['🦋', 'Butterfly', 'change transform'],
  ['🌻', 'Sunflower', 'bright flower'],
  ['🍀', 'Clover', 'success lucky'],
  ['🌈', 'Rainbow', 'color visual'],
  ['🐙', 'Octopus', 'many integration'],
  ['🦊', 'Fox', 'smart quick'],
  ['🐝', 'Bee', 'busy worker'],
  ['🦄', 'Unicorn', 'unique special'],
  ['🐳', 'Whale', 'large data'],
  ['🍋', 'Lemon', 'yellow fresh'],
  ['🍓', 'Strawberry', 'red fruit'],
  ['🍄', 'Mushroom', 'nature'],
  ['🌋', 'Volcano', 'failure pressure'],
  ['🏔️', 'Mountain', 'challenge'],
  ['🚲', 'Bicycle', 'flow movement'],
  ['🚀', 'Rocket', 'launch deploy'],
  ['🎈', 'Balloon', 'light celebration'],
  ['🎨', 'Palette', 'design color'],
  ['🎲', 'Dice', 'random flaky'],
  ['🎯', 'Target', 'goal precision'],
  ['🎹', 'Piano', 'keyboard input'],
  ['📚', 'Books', 'documentation'],
  ['✏️', 'Pencil', 'edit author'],
  ['🪐', 'Planet', 'environment'],
  ['🌙', 'Moon', 'dark theme'],
  ['☀️', 'Sun', 'light theme'],
  ['⭐', 'Star', 'favorite quality'],
  ['🔔', 'Bell', 'notification'],
  ['🔬', 'Microscope', 'detail inspect'],
  ['💎', 'Gem', 'quality durable']
] as const

const PersistedEmoji = z.object({
  emoji: z.string(),
  scenarioHash: z.string().regex(/^[a-f0-9]{64}$/),
  path: z.string().min(1)
})

export async function persistScenarioEmoji(input: {
  scenarioId: string
  emoji: string
  expectedScenarioHash: string
  fetcher?: typeof fetch
}): Promise<z.infer<typeof PersistedEmoji>> {
  const response = await (input.fetcher ?? fetch)('/__blume/scenarios/emoji', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      scenarioId: input.scenarioId,
      emoji: input.emoji,
      expectedScenarioHash: input.expectedScenarioHash
    })
  })
  const payload = (await response.json().catch(() => ({}))) as {
    message?: string
  }
  if (!response.ok) {
    throw new Error(
      payload.message ??
        (response.status === 404
          ? 'Repository authoring is available only from the local dashboard.'
          : `Scenario emoji update failed with HTTP ${response.status}.`)
    )
  }
  return PersistedEmoji.parse(payload)
}

export function ScenarioEmojiPicker({
  scenarioId,
  initialEmoji,
  expectedScenarioHash
}: {
  scenarioId: string
  initialEmoji: string
  expectedScenarioHash?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [emoji, setEmoji] = useState(initialEmoji)
  const [scenarioHash, setScenarioHash] = useState(expectedScenarioHash)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const choices = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return normalized
      ? emojiChoices.filter((choice) => choice.join(' ').toLowerCase().includes(normalized))
      : emojiChoices
  }, [query])

  function close(): void {
    setOpen(false)
    setQuery('')
  }

  async function select(nextEmoji: string): Promise<void> {
    if (!scenarioHash) {
      setError('Load a persisted run definition before editing this authored scenario.')
      return
    }
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const result = await persistScenarioEmoji({
        scenarioId,
        emoji: nextEmoji,
        expectedScenarioHash: scenarioHash
      })
      setEmoji(result.emoji)
      setScenarioHash(result.scenarioHash)
      setNotice(`Saved to ${result.path}. Review and commit this repository change.`)
      close()
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Scenario emoji update failed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) {
          setError('')
        } else {
          setQuery('')
        }
      }}
    >
      <div className="relative shrink-0">
        <DialogTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={`Change emoji for scenario ${scenarioId}`}
            className="rounded-[32%] focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            <AvatarTile id={scenarioId} emoji={emoji} size="large" />
          </button>
        </DialogTrigger>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            searchRef.current?.focus()
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            triggerRef.current?.focus()
          }}
          className="block w-72 p-2"
        >
          <DialogTitle className="sr-only">Choose emoji for {scenarioId}</DialogTitle>
          <div className="flex items-center gap-2 border-b px-2 pb-2">
            <Search aria-hidden className="size-4 text-muted-foreground" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search emoji…"
              aria-label="Search emoji"
              className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground"
            />
            <button
              type="button"
              onClick={close}
              aria-label="Close emoji picker"
              className="rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              <X aria-hidden className="size-4" />
            </button>
          </div>
          <div className="mt-2 grid max-h-64 grid-cols-6 gap-1 overflow-y-auto" role="listbox">
            {choices.map(([choice, label, keywords]) => (
              <button
                key={choice}
                type="button"
                role="option"
                aria-selected={choice === emoji}
                aria-label={label}
                title={`${label} — ${keywords}`}
                disabled={saving || !scenarioHash}
                onClick={() => void select(choice)}
                className="flex size-9 items-center justify-center rounded-md text-lg hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50 aria-selected:bg-accent"
              >
                {choice}
              </button>
            ))}
          </div>
          {choices.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              No matching emoji.
            </p>
          ) : null}
          <p className="mt-2 border-t px-2 pt-2 text-xs text-muted-foreground">
            {scenarioHash
              ? 'Saves to the authored scenario YAML through the local developer adapter.'
              : 'A persisted run definition is required to guard the authored YAML update.'}
          </p>
          {error ? (
            <p role="alert" className="mt-2 px-2 text-xs text-status-fail">
              {error}
            </p>
          ) : null}
        </DialogContent>
        <p aria-live="polite" className="sr-only">
          {saving ? 'Saving scenario emoji.' : notice}
        </p>
      </div>
    </Dialog>
  )
}
